import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import {
  RefreshCw, AlertTriangle, Layers, MousePointerClick, Trophy, ArrowUpDown,
  ExternalLink, Image as ImageIcon, Info, ChevronDown, ChevronRight, Video, Users,
  LayoutGrid, MonitorPlay, MessageSquare,
} from 'lucide-react';

const API = 'https://api.glowytics.com/api';

/**
 * Meta Karusel Kart Performansı
 * ---------------------------------------------------------------------------
 * Karuselin hangi kartının tıklandığı Meta'da `action_carousel_card_name`
 * action-breakdown'ıyla gelir; `image_asset` breakdown'ı karuselde BOŞ döner.
 *
 * Sayfanın saklamaması gereken iki şey:
 *  - Link tıklamalarının yarıdan fazlası `unknown` kovasına düşer → mutlak
 *    sayı değil, atfedilenler içindeki PAY konuşulur.
 *  - Gösterim kart bazında gelmez → kart CTR'ı hesaplanamaz, gösterilmez.
 */

const fmtNum = (n) => (Number(n) || 0).toLocaleString('tr-TR');
const fmtMoney = (n) => (Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 }) + ' ₺';
const fmtPct = (n) => (Number(n) * 100).toLocaleString('tr-TR', { maximumFractionDigits: 1 }) + '%';
const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? '—' : d.toLocaleDateString('tr-TR');
};

const statusTone = (s = '') => {
  const t = s.toUpperCase();
  if (t === 'ACTIVE') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (t === 'PAUSED') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-gray-50 text-gray-600 border-gray-200';
};

const Stat = ({ label, value, sub, tone = 'text-gray-900' }) => (
  <div className="bg-white border border-gray-200 rounded-lg px-3 py-2.5">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-lg font-semibold ${tone}`}>{value}</div>
    {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const MEDAL = ['bg-amber-400 text-amber-950', 'bg-slate-300 text-slate-800', 'bg-orange-300 text-orange-950'];

function CardTile({ card, rank, max, showRank, adCount = 1 }) {
  const pct = max > 0 ? card.linkClicks / max : 0;
  const top = showRank && rank < 3 && card.linkClicks > 0;
  return (
    <div
      className={`rounded-lg border bg-white overflow-hidden flex flex-col ${
        top ? 'border-amber-300 ring-2 ring-amber-100' : 'border-gray-200'
      }`}
    >
      <div className="relative bg-gray-100 aspect-square">
        {card.imageUrl ? (
          <img
            src={card.imageUrl}
            alt={card.name || `Kart ${card.index}`}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            {card.videoId ? <Video size={28} /> : <ImageIcon size={28} />}
          </div>
        )}
        <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/65 text-white text-[11px] font-semibold">
          {card.positions.length > 1 ? `Sıra ${card.positions.join('/')}` : `Kart ${card.index}`}
        </span>
        {top && (
          <span className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold ${MEDAL[rank]}`}>
            {rank + 1}
          </span>
        )}
      </div>

      <div className="p-2.5 flex-1 flex flex-col gap-1.5">
        <div className="text-[12px] font-medium text-gray-800 leading-snug line-clamp-2" title={card.name}>
          {card.name || <span className="text-gray-400">başlıksız</span>}
        </div>

        <div className="mt-auto">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xl font-bold text-gray-900">{fmtNum(card.linkClicks)}</span>
            <span className="text-[12px] font-semibold text-blue-700">{fmtPct(card.share)}</span>
          </div>
          <div className="text-[10px] text-gray-500 -mt-0.5">link tıklama · atfedilen pay</div>
          <div className="h-1.5 bg-gray-100 rounded mt-1.5 overflow-hidden">
            <div
              className={`h-full rounded ${top ? 'bg-amber-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.max(pct * 100, card.linkClicks > 0 ? 2 : 0)}%` }}
            />
          </div>
        </div>

        {adCount > 1 && card.perAd && card.perAd.length > 1 && (
          <div className="text-[10px] text-gray-400">{card.perAd.length} reklamda kullanıldı</div>
        )}

        {card.link && (
          <a
            href={card.link}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] text-gray-400 hover:text-blue-600 truncate inline-flex items-center gap-1"
            title={card.link}
          >
            <ExternalLink size={10} /> {card.link.replace(/^https?:\/\//, '')}
          </a>
        )}
      </div>
    </div>
  );
}

function CardGrid({ cards, showRank, adCount = 1 }) {
  const max = Math.max(0, ...cards.map((c) => c.linkClicks));
  const rankOf = new Map();
  [...cards]
    .sort((a, b) => b.linkClicks - a.linkClicks || a.index - b.index)
    .forEach((c, i) => rankOf.set(c.key, i));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {cards.map((c) => (
        <CardTile
          key={c.key}
          card={c}
          rank={rankOf.get(c.key)}
          max={max}
          showRank={showRank}
          adCount={adCount}
        />
      ))}
    </div>
  );
}

/**
 * One ad = one labelled block. Without this, a campaign or ad set holding
 * several creatives renders as an untraceable pile of thumbnails.
 */
function AdGroup({ ad, cards, showRank }) {
  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50/60 overflow-hidden">
      <div className="px-3 py-2 bg-white border-b border-gray-200 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-gray-900 flex items-center gap-1.5">
            <MonitorPlay size={14} className="text-blue-600 shrink-0" />
            <span className="truncate">{ad.adName}</span>
            <span className={`px-1.5 py-0.5 rounded border text-[10px] font-medium ${statusTone(ad.status)}`}>
              {ad.status}
            </span>
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <Users size={10} className="text-gray-400" />
            <span>{ad.adsetName}</span>
            <span>·</span>
            <span>{ad.cards.length} kart</span>
            <span>·</span>
            <span className="text-gray-400">ID {ad.adId}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 text-right shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Gösterim</div>
            <div className="text-[13px] font-semibold text-gray-800">{fmtNum(ad.impressions)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Harcama</div>
            <div className="text-[13px] font-semibold text-gray-800">{fmtMoney(ad.spend)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Karta atfedilen</div>
            <div className="text-[13px] font-semibold text-blue-700">
              {fmtNum(ad.attributedLinkClicks)}
              <span className="text-[10px] font-normal text-gray-500"> / {fmtNum(ad.linkClicks)}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-3">
        <CardGrid cards={cards} showRank={showRank} />
        <div className="text-[10px] text-gray-500 mt-2">
          Paylar bu reklamın kendi atfedilen {fmtNum(ad.attributedLinkClicks)} tıklamasına göre hesaplandı.
          {ad.unknownLinkClicks > 0 && ` ${fmtNum(ad.unknownLinkClicks)} tıklama karta bağlanamadı.`}
        </div>
      </div>
    </div>
  );
}

/**
 * DM (click-to-message) ads.
 *
 * Deliberately a separate tab, not a column on the carousel view: Meta's
 * carousel card breakdown returns link_click ONLY, so per-card message counts
 * do not exist. What does exist is per-CREATIVE, and these ads carry one
 * creative each — so ad == image and the ranking is real.
 */
function MessagingPanel({ data, loading, sort, setSort, ads }) {
  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg p-10 text-center text-gray-500 text-sm bg-white">
        Mesaj reklamları çekiliyor…
      </div>
    );
  }
  if (!data) return null;
  if (!ads.length) {
    return (
      <div className="border border-dashed border-gray-300 rounded-lg p-10 text-center text-gray-500 text-sm">
        Bu dönemde mesaj (DM) hedefli reklam bulunamadı.
      </div>
    );
  }

  const t = data.totals;
  const bestCost = Math.min(...ads.map((a) => (a.costPerConversation == null ? Infinity : a.costPerConversation)));
  const maxConv = Math.max(...ads.map((a) => a.conversations));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="Harcama" value={fmtMoney(t.spend)} sub={`${data.adsetCount} reklam seti`} />
        <Stat label="Tıklama" value={fmtNum(t.linkClicks)} />
        <Stat label="Başlayan sohbet" value={fmtNum(t.conversations)} tone="text-emerald-700" />
        <Stat label="Tık → mesaj" value={fmtPct(t.clickToMessage)} sub="tıklamanın mesaja dönme oranı" />
        <Stat
          label="Sohbet maliyeti"
          value={t.costPerConversation == null ? '—' : fmtMoney(t.costPerConversation)}
          tone="text-blue-700"
        />
      </div>

      <div className="border border-blue-200 bg-blue-50 rounded-lg px-3 py-2.5 text-[12px] text-blue-900 flex gap-2">
        <Info size={15} className="mt-0.5 shrink-0" />
        <div className="space-y-1">
          <div>
            <b>Tıklama ile mesaj aynı şey değil.</b> Bu hesapta tıklamaların yalnızca {fmtPct(t.clickToMessage)}'i
            sohbete dönüyor ve oran kreatiften kreatife katlanarak değişiyor. En çok tıklanan reklam, en çok mesaj
            getiren reklam olmayabilir.
          </div>
          <div>
            Bu reklamların her biri <b>tek kreatif</b> taşıyor, yani burada bir satır bir görsele/videoya eşit.
            Karusel kartı bazında mesaj sayısı Meta API'de <b>yok</b>: kart kırılımı sadece link tıklaması döndürüyor.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
          <MessageSquare size={15} className="text-emerald-600" />
          Kreatif bazında mesaj performansı
        </div>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-gray-500 mr-1">Sırala:</span>
          {[
            { k: 'conversations', label: 'Sohbet' },
            { k: 'cost', label: 'Sohbet maliyeti' },
            { k: 'rate', label: 'Tık → mesaj' },
            { k: 'spend', label: 'Harcama' },
          ].map((o) => (
            <button
              key={o.k}
              onClick={() => setSort(o.k)}
              className={`px-2 py-1 rounded border ${
                sort === o.k
                  ? 'border-blue-300 bg-blue-50 text-blue-800'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {ads.map((a, i) => {
          const best = a.costPerConversation != null && a.costPerConversation === bestCost;
          return (
            <div
              key={a.adId}
              className={`border rounded-lg bg-white overflow-hidden flex ${
                best ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-gray-200'
              }`}
            >
              <div className="w-28 shrink-0 bg-gray-100 relative">
                {a.thumbnailUrl ? (
                  <img src={a.thumbnailUrl} alt={a.adName} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-gray-400">
                    {a.creativeKind === 'VIDEO' ? <Video size={22} /> : <ImageIcon size={22} />}
                  </div>
                )}
                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/65 text-white text-[10px] font-semibold">
                  {i + 1}
                </span>
                {a.creativeKind === 'VIDEO' && (
                  <span className="absolute bottom-1 left-1 px-1 py-0.5 rounded bg-black/60 text-white text-[9px] inline-flex items-center gap-0.5">
                    <Video size={9} /> video
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0 p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold text-gray-900 leading-snug line-clamp-2" title={a.adName}>
                      {a.adName}
                    </div>
                    <div className="text-[10px] text-gray-500 truncate" title={a.adsetName}>
                      {a.adsetName}
                    </div>
                  </div>
                  <span className={`px-1.5 py-0.5 rounded border text-[9px] shrink-0 ${statusTone(a.status)}`}>
                    {a.status}
                  </span>
                </div>

                <div className="mt-2 grid grid-cols-3 gap-1.5 text-center">
                  <div className="bg-gray-50 rounded py-1">
                    <div className="text-[9px] uppercase text-gray-500">Sohbet</div>
                    <div className="text-[15px] font-bold text-emerald-700">{fmtNum(a.conversations)}</div>
                  </div>
                  <div className="bg-gray-50 rounded py-1">
                    <div className="text-[9px] uppercase text-gray-500">Tık → mesaj</div>
                    <div className="text-[15px] font-bold text-gray-900">{fmtPct(a.clickToMessage)}</div>
                  </div>
                  <div className={`rounded py-1 ${best ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                    <div className="text-[9px] uppercase text-gray-500">Sohbet maliyeti</div>
                    <div className={`text-[15px] font-bold ${best ? 'text-emerald-700' : 'text-gray-900'}`}>
                      {a.costPerConversation == null ? '—' : fmtMoney(a.costPerConversation)}
                    </div>
                  </div>
                </div>

                <div className="h-1.5 bg-gray-100 rounded mt-2 overflow-hidden">
                  <div
                    className="h-full rounded bg-emerald-500"
                    style={{ width: `${maxConv > 0 ? (a.conversations / maxConv) * 100 : 0}%` }}
                  />
                </div>

                <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] text-gray-500">
                  <span>{fmtMoney(a.spend)}</span>
                  <span>·</span>
                  <span>{fmtNum(a.impressions)} gösterim</span>
                  <span>·</span>
                  <span>{fmtNum(a.linkClicks)} tıklama</span>
                  <span>·</span>
                  <span title="Sohbeti ikinci mesaja taşıyan kişi sayısı">
                    2. mesaj {fmtNum(a.depth2)} ({fmtPct(a.depth2Rate)})
                  </span>
                  {a.blocks > 0 && (
                    <>
                      <span>·</span>
                      <span className="text-rose-600">{fmtNum(a.blocks)} engelleme</span>
                    </>
                  )}
                </div>

                {a.isCarousel && (
                  <div className="mt-1.5 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 flex gap-1">
                    <Layers size={11} className="mt-px shrink-0" />
                    <span>
                      <b>{a.cardCount} kartlı karusel.</b> Meta mesajı kart bazında vermiyor — kart kırılımı yalnızca
                      link tıklaması döndürüyor, mesaj aksiyonu hiç gelmiyor. Buradaki {fmtNum(a.conversations)} sohbet
                      reklamın tamamına ait.
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MetaCarouselCards() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId') || '';

  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [days, setDays] = useState('180');
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [sortByClicks, setSortByClicks] = useState(true);
  const [showAds, setShowAds] = useState(false);
  const [groupByAd, setGroupByAd] = useState(true);
  const [tab, setTab] = useState('carousel');
  const [msg, setMsg] = useState(null);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [msgSort, setMsgSort] = useState('conversations');
  const [openCamps, setOpenCamps] = useState({});
  const [openSets, setOpenSets] = useState({});

  // One key for all three levels, so the picker can highlight whichever the
  // user actually clicked: campaign, ad set, or a single ad.
  const keyOf = (t) => (!t ? null : t.adId ? `ad:${t.adId}` : t.adsetId ? `set:${t.adsetId}` : `camp:${t.campaignId}`);
  const selKey = keyOf(selected);

  const headers = useMemo(() => ({ 'X-Brand-ID': brandId }), [brandId]);
  const qs = useMemo(
    () => `brandId=${brandId}&days=${days}${accountId ? `&accountId=${accountId}` : ''}`,
    [brandId, days, accountId]
  );

  useEffect(() => {
    if (!brandId) return;
    fetch(`${API}/meta-carousel/accounts?brandId=${brandId}`, { credentials: 'include', headers })
      .then((r) => r.json())
      .then((j) => setAccounts(j.accounts || []))
      .catch(() => {});
  }, [brandId, headers]);

  const loadList = useCallback(async () => {
    if (!brandId) return;
    setLoadingList(true);
    setError(null);
    setData(null);
    setSelected(null);
    try {
      const res = await fetch(`${API}/meta-carousel/ads?${qs}`, { credentials: 'include', headers });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setList(j);
      (j.warnings || []).forEach((w) => toast(w, { icon: '⚠️' }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingList(false);
    }
  }, [brandId, qs, headers]);

  useEffect(() => { loadList(); }, [loadList]);

  const loadCards = useCallback(
    async (target) => {
      setSelected(target);
      setLoadingData(true);
      setError(null);
      setData(null);
      try {
        const scope = target.adId
          ? `adId=${encodeURIComponent(target.adId)}`
          : target.adsetId
          ? `adsetId=${encodeURIComponent(target.adsetId)}`
          : `campaignId=${encodeURIComponent(target.campaignId)}`;
        const res = await fetch(
          `${API}/meta-carousel/cards?${qs}&${scope}`,
          { credentials: 'include', headers }
        );
        const j = await res.json();
        if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
        setData(j);
        (j.warnings || []).forEach((w) => toast(w, { icon: '⚠️' }));
      } catch (e) {
        setError(e.message);
        toast.error(e.message);
      } finally {
        setLoadingData(false);
      }
    },
    [qs, headers]
  );

  // DM tab loads on first visit and on every period/account change.
  useEffect(() => {
    if (tab !== 'messaging' || !brandId) return;
    let dead = false;
    setLoadingMsg(true);
    setError(null);
    fetch(`${API}/meta-carousel/messaging?${qs}`, { credentials: 'include', headers })
      .then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        return j;
      })
      .then((j) => {
        if (dead) return;
        setMsg(j);
        (j.warnings || []).forEach((w) => toast(w, { icon: '⚠️' }));
      })
      .catch((e) => !dead && setError(e.message))
      .finally(() => !dead && setLoadingMsg(false));
    return () => {
      dead = true;
    };
  }, [tab, brandId, qs, headers]);

  const msgAds = useMemo(() => {
    if (!msg) return [];
    const a = [...msg.ads];
    if (msgSort === 'cost') {
      return a.sort((x, y) => {
        if (x.costPerConversation == null) return 1;
        if (y.costPerConversation == null) return -1;
        return x.costPerConversation - y.costPerConversation;
      });
    }
    if (msgSort === 'rate') return a.sort((x, y) => y.clickToMessage - x.clickToMessage);
    if (msgSort === 'spend') return a.sort((x, y) => y.spend - x.spend);
    return a.sort((x, y) => y.conversations - x.conversations);
  }, [msg, msgSort]);

  const sortCards = useCallback(
    (arr) => {
      const c = [...arr];
      return sortByClicks
        ? c.sort((a, b) => b.linkClicks - a.linkClicks || a.index - b.index)
        : c.sort((a, b) => a.index - b.index);
    },
    [sortByClicks]
  );

  // Grouped only makes sense with more than one ad in scope.
  const grouped = !!data && data.ads.length > 1 && groupByAd;

  const cards = useMemo(() => (data ? sortCards(data.cards) : []), [data, sortCards]);

  if (!brandId) return <div className="p-8 text-gray-500">Önce bir marka seçin.</div>;

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Layers size={20} className="text-blue-600" />
            Karusel Kart Performansı
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Karuselin hangi görselinin tıklandığı. {list?.accountName ? `${list.accountName} · ` : ''}
            {list ? `${list.totalAdsScanned} reklam tarandı` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {accounts.length > 1 && (
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
            >
              <option value="">Varsayılan hesap</option>
              {accounts.map((a) => (
                <option key={a.accountId} value={a.accountId}>{a.name}</option>
              ))}
            </select>
          )}
          <select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
          >
            <option value="30">Son 30 gün</option>
            <option value="90">Son 90 gün</option>
            <option value="180">Son 180 gün</option>
            <option value="365">Son 1 yıl</option>
            <option value="all">Tüm zamanlar</option>
          </select>
          <button
            onClick={loadList}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            <RefreshCw size={14} className={loadingList ? 'animate-spin' : ''} /> Yenile
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 border border-rose-200 bg-rose-50 text-rose-800 rounded px-3 py-2 text-sm">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {/* tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
        {[
          { k: 'carousel', label: 'Karusel kartları', icon: Layers },
          { k: 'messaging', label: 'Mesaj (DM) reklamları', icon: MessageSquare },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.k
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <t.icon size={15} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'messaging' && (
        <MessagingPanel
          data={msg}
          loading={loadingMsg}
          sort={msgSort}
          setSort={setMsgSort}
          ads={msgAds}
        />
      )}

      {tab === 'carousel' && (
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* campaign picker */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden self-start">
          <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Karusel kampanyaları {list ? `(${list.campaigns.length})` : ''}
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
            {loadingList && <div className="p-4 text-sm text-gray-500">Yükleniyor…</div>}
            {!loadingList && list && list.campaigns.length === 0 && (
              <div className="p-4 text-sm text-gray-500">Bu dönemde karusel reklam yok.</div>
            )}
            {(list?.campaigns || []).map((c) => {
              const ck = c.campaignId || c.campaignName;
              const open = !!openCamps[ck];
              return (
                <div key={ck}>
                  {/* campaign */}
                  <div className={`flex items-stretch ${selKey === `camp:${c.campaignId}` ? 'bg-blue-50 border-l-2 border-blue-600' : ''}`}>
                    <button onClick={() => loadCards(c)} className="flex-1 text-left px-3 py-2.5 hover:bg-blue-50 transition">
                      <div className="text-[13px] font-medium text-gray-800 leading-snug">{c.campaignName}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span>{c.cardCount} kart</span>
                        <span>·</span>
                        <span>{c.adsets.length} set</span>
                        <span>·</span>
                        <span>{c.adCount} reklam</span>
                        <span>·</span>
                        <span>{fmtMoney(c.spend)}</span>
                        <span>·</span>
                        <span>{fmtNum(c.linkClicks)} tıklama</span>
                      </div>
                    </button>
                    <button
                      onClick={() => setOpenCamps((o) => ({ ...o, [ck]: !o[ck] }))}
                      title="Reklam setlerini gör"
                      className="px-2 text-gray-400 hover:text-gray-700 hover:bg-gray-50"
                    >
                      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  </div>

                  {/* ad sets */}
                  {open &&
                    c.adsets.map((st) => {
                      const sk = st.adsetId || st.adsetName;
                      const sOpen = !!openSets[sk];
                      return (
                        <div key={sk} className="border-t border-gray-50">
                          <div className={`flex items-stretch ${selKey === `set:${st.adsetId}` ? 'bg-blue-50 border-l-2 border-blue-600' : ''}`}>
                            <button onClick={() => loadCards(st)} className="flex-1 text-left pl-5 pr-2 py-2 hover:bg-blue-50 transition">
                              <div className="text-[12px] font-medium text-gray-700 leading-snug flex items-center gap-1.5">
                                <Users size={11} className="text-gray-400 shrink-0" />
                                <span className="truncate">{st.adsetName}</span>
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5 pl-4">
                                {st.cardCount} kart · {st.adCount} reklam · {fmtMoney(st.spend)} · {fmtNum(st.linkClicks)} tıklama
                              </div>
                            </button>
                            {st.ads.length > 1 && (
                              <button
                                onClick={() => setOpenSets((o) => ({ ...o, [sk]: !o[sk] }))}
                                title="Reklamları gör"
                                className="px-2 text-gray-300 hover:text-gray-700 hover:bg-gray-50"
                              >
                                {sOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                              </button>
                            )}
                          </div>

                          {/* ads */}
                          {sOpen &&
                            st.ads.map((a) => (
                              <button
                                key={a.adId}
                                onClick={() => loadCards(a)}
                                className={`w-full text-left pl-10 pr-3 py-1.5 hover:bg-blue-50 transition border-t border-gray-50 ${
                                  selKey === `ad:${a.adId}` ? 'bg-blue-50 border-l-2 border-blue-600' : ''
                                }`}
                              >
                                <div className="text-[11px] text-gray-700 leading-snug truncate">{a.adName}</div>
                                <div className="text-[10px] text-gray-400 mt-0.5">
                                  {a.cardCount} kart · {fmtMoney(a.spend)} · {fmtNum(a.linkClicks)} tıklama
                                </div>
                              </button>
                            ))}
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </div>

        {/* detail */}
        <div>
          {!selected && !loadingData && (
            <div className="border border-dashed border-gray-300 rounded-lg p-10 text-center text-gray-500 text-sm">
              Soldan bir karusel kampanyası seçin.
            </div>
          )}
          {loadingData && (
            <div className="border border-gray-200 rounded-lg p-10 text-center text-gray-500 text-sm bg-white">
              Meta'dan kart kırılımı çekiliyor…
            </div>
          )}

          {data && (
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  {data.scope === 'adset' ? data.adsetName : data.campaignName}
                </h2>
                <div className="text-xs text-gray-500">
                  {data.scope === 'adset' && <span className="text-gray-400">{data.campaignName} · </span>}
                  <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-medium mr-1">
                    {data.scope === 'ad' ? 'REKLAM' : data.scope === 'adset' ? 'REKLAM SETİ' : 'KAMPANYA'}
                  </span>
                  {data.scope === 'ad' ? '1 reklam' : `${data.adCount} karusel reklam birleşik`} ·{' '}
                  {data.cards.length} kart · {fmtDate(data.period.dateStart)} – {fmtDate(data.period.dateStop)}
                </div>
                {data.scope !== 'ad' && data.adCount > 1 && (
                  <div className="text-[11px] text-gray-500 mt-1">
                    Birden fazla reklam birleştirildi; aynı görsel farklı reklamlarda görünüyorsa tıklamaları toplanır.
                    Daha dar bakmak için soldaki oku açıp reklam setini veya tek reklamı seçin.
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <Stat label="Gösterim" value={fmtNum(data.totals.impressions)} />
                <Stat label="Harcama" value={fmtMoney(data.totals.spend)} />
                <Stat label="Link tıklama" value={fmtNum(data.unattributed.allLinkClicks)} sub="tüm kartlar dahil" />
                <Stat
                  label="Karta atfedilen"
                  value={fmtNum(data.unattributed.attributedLinkClicks)}
                  sub={`toplamın ${fmtPct(data.unattributed.attributionRate)}'i`}
                  tone="text-blue-700"
                />
                <Stat
                  label="Atfedilemeyen"
                  value={fmtNum(data.unattributed.unknownLinkClicks)}
                  sub="unknown kovası"
                  tone="text-amber-700"
                />
              </div>

              {/* the caveat, stated on the page and not in a tooltip */}
              <div className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-2.5 text-[12px] text-amber-900 flex gap-2">
                <Info size={15} className="mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <div>
                    <b>Tıklamaların {fmtPct(1 - data.unattributed.attributionRate)}'i Meta tarafından bir karta bağlanamıyor.</b>{' '}
                    Bu yüzden kartların mutlak tıklama sayısı değil, <b>birbirlerine göre payı</b> anlamlıdır.
                  </div>
                  <div>
                    Meta gösterimi kart bazında vermiyor, dolayısıyla <b>kart CTR'ı hesaplanamaz</b>. Ayrıca kart 1 doğal
                    olarak avantajlıdır — kullanıcı kaydırmadan görür. Sıra etkisini not ederek okuyun.
                  </div>
                  {data.unattributed.endCardLinkClicks > 0 && (
                    <div>
                      Bitiş/sayfa kartı ({data.unattributed.endCardName}):{' '}
                      <b>{fmtNum(data.unattributed.endCardLinkClicks)}</b> tıklama — aşağıdaki görsellere dahil değil.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Trophy size={15} className="text-amber-500" />
                  {sortByClicks ? 'En çok tıklama alan kartlar' : 'Karusel sırasıyla kartlar'}
                </div>
                <div className="flex items-center gap-2">
                  {data.ads.length > 1 && (
                    <button
                      onClick={() => setGroupByAd((g) => !g)}
                      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border ${
                        groupByAd
                          ? 'border-blue-300 bg-blue-50 text-blue-800'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <LayoutGrid size={13} />
                      {groupByAd ? 'Reklam bazında gruplu' : 'Tüm kartlar birleşik'}
                    </button>
                  )}
                  <button
                    onClick={() => setSortByClicks((s) => !s)}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    <ArrowUpDown size={13} />
                    {sortByClicks ? 'Karusel sırasına göre' : 'Tıklamaya göre'}
                  </button>
                </div>
              </div>

              {grouped ? (
                <div className="space-y-4">
                  {[...data.ads]
                    .sort((a, b) => b.spend - a.spend)
                    .map((ad) => (
                      <AdGroup key={ad.adId} ad={ad} cards={sortCards(ad.cards)} showRank={sortByClicks} />
                    ))}
                </div>
              ) : (
                <CardGrid cards={cards} showRank={sortByClicks} adCount={data.ads.length} />
              )}

              {/* per-ad detail */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowAds((s) => !s)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left"
                >
                  {showAds ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  <MousePointerClick size={15} className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Reklam bazında kırılım</span>
                  <span className="text-xs text-gray-400">({data.ads.length} reklam)</span>
                </button>
                {showAds && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="w-full text-[12px]">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Reklam</th>
                          <th className="text-right px-2 py-2 font-medium">Gösterim</th>
                          <th className="text-right px-2 py-2 font-medium">Harcama</th>
                          <th className="text-right px-2 py-2 font-medium">Link tıklama</th>
                          <th className="text-left px-3 py-2 font-medium">Kart bazında</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {data.ads.map((a) => (
                          <tr key={a.adId} className="align-top">
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">{a.adName}</div>
                              <div className="text-[10px] text-gray-500">{a.adsetName}</div>
                              <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded border text-[10px] ${statusTone(a.status)}`}>
                                {a.status}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">{fmtNum(a.impressions)}</td>
                            <td className="px-2 py-2 text-right tabular-nums">{fmtMoney(a.spend)}</td>
                            <td className="px-2 py-2 text-right tabular-nums">{fmtNum(a.linkClicks)}</td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap gap-1">
                                {a.cardClicks.map((c) => (
                                  <span
                                    key={c.index}
                                    title={c.name}
                                    className={`px-1.5 py-0.5 rounded border text-[10px] ${
                                      c.linkClicks > 0
                                        ? 'bg-blue-50 border-blue-200 text-blue-800'
                                        : 'bg-gray-50 border-gray-200 text-gray-400'
                                    }`}
                                  >
                                    K{c.index}: {fmtNum(c.linkClicks)}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
